/**
 * Survey Viewer Dashboard
 * Read-only access to surveys and their responses
 * Cannot create or edit surveys
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../api/axios';
import { SurveyData } from '../App';

export function ViewerDashboard() {
  const navigate = useNavigate();
  const [surveys, setSurveys] = useState<SurveyData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSurveys();
  }, []);

  const fetchSurveys = async () => {
    try {
      const response = await axios.get('/surveys');
      setSurveys(response.data);
    } catch (error) {
      console.error('Error fetching surveys:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-primary)' }}>
        Loading surveys...
      </div>
    );
  }

  return (
    <div>
      <div style={{
        marginBottom: '2rem',
        padding: '1.5rem',
        background: 'var(--card-bg)',
        border: '1px solid var(--card-border)',
        borderRadius: '12px'
      }}>
        <h2 style={{ margin: '0 0 0.5rem 0', color: 'var(--accent)', fontSize: '28px' }}>
          👁️ Survey Viewer
        </h2>
        <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
          Read-only access: You can view surveys and responses but cannot create or edit
        </p>
      </div>

      {surveys.length === 0 ? (
        <div style={{
          background: 'var(--card-bg)',
          border: '2px dashed var(--card-border)',
          borderRadius: '12px',
          padding: '3rem',
          textAlign: 'center',
          color: 'var(--text-secondary)'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '1rem' }}>📊</div>
          <h3 style={{ color: 'var(--text-primary)', marginBottom: '0.5rem' }}>No surveys available</h3>
          <p>There are currently no surveys to view</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '1rem' }}>
          {surveys.map((survey) => (
            <div
              key={survey.id}
              style={{
                background: 'var(--card-bg)',
                border: '1px solid var(--card-border)',
                borderRadius: '12px',
                padding: '1.5rem'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: '0 0 0.5rem 0', color: 'var(--text-primary)' }}>
                    {survey.title}
                  </h3>
                  <p style={{ margin: '0 0 1rem 0', color: 'var(--text-secondary)', fontSize: '14px' }}>
                    {survey.description || 'No description'}
                  </p>
                  <div style={{ display: 'flex', gap: '1rem', fontSize: '13px', color: 'var(--text-secondary)' }}>
                    {survey.creator_name && <span>👤 Created by: {survey.creator_name}</span>}
                    <span>
                      {survey.is_published ? (
                        <span style={{ color: 'var(--success)' }}>✓ This survey is Published</span>
                      ) : (
                        <span style={{ color: 'var(--warning)' }}>○ This survey is a Draft</span>
                      )}
                    </span>
                    {survey.created_at && <span>📅 {new Date(survey.created_at).toLocaleDateString()}</span>}
                  </div>
                </div>
                <button
                  onClick={() => navigate(`/Surveys/responses/${survey.id}`)}
                  style={{
                    padding: '8px 16px',
                    background: 'var(--button-bg)',
                    color: 'var(--button-text)',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: 'pointer',
                    transition: 'all 0.3s'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.opacity = '0.8';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.opacity = '1';
                  }}
                >
                  View Responses
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
